'use client'

import { useRef, useEffect, useLayoutEffect, useState } from 'react'
import { Check, Clock, ExternalLink, Loader2, Mail, Phone, RefreshCw } from 'lucide-react'
import { usePermissions } from '@/hooks/use-permissions'
import { AI_PERMISSIONS, MESSAGING_AGENTS_PERMISSIONS } from '@/lib/auth/permissions.constants'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { MessageBubble } from './message-bubble'
import { ConversationDateSeparator } from '@/components/ui/conversation/conversation-date-separator'
import { ConversationEmptyState } from '@/components/ui/conversation/conversation-empty-state'
import { ConversationEventLine } from '@/components/ui/conversation/conversation-events'
import { ConversationComposer } from '@/components/ui/conversation/conversation-composer'
import { ConversationHeader, HEADER_CONTROL_STYLES } from '@/components/ui/conversation/conversation-header'
import { OutcomeCorrectionDialog } from '@/components/ui/conversation/outcome-correction-dialog'
import { sessionSummaryFacts } from '@/lib/conversation-quality/outcome-display'
import { InboxBackButton } from '@/components/ui/conversation/inbox-layout'
import {
  messagingStatusBadge,
  messagingThreadActions,
  messagingThreadBanners,
  type MessagingConversationState,
} from '@/lib/conversation/messaging-thread-header'
import type { ThreadAction, ThreadActionId } from '@/lib/conversation/thread-header'
import {
  placeAfterAnsweredMessages,
  toConversationEvent,
  type ConversationEvent,
} from '@/lib/conversation/conversation-events'
import { ExcludeContactDialog } from './exclude-contact-dialog'
import { isRenderableMessage, type ConversationDetail, type ConversationMessage } from '@/hooks/agents/messaging/use-messages'
import { cn } from '@/lib/utils'
import { resolveContactTitle } from '@/lib/agents/messaging/contact-title'
import { exclusionIdentity } from '@/lib/agents/messaging/contact-exclusions'
import { resolveThreadScrollAction } from '@/lib/agents/messaging/thread-scroll'
import { formatClockTime, groupByDayInZone, isTodayInZone } from '@/lib/conversation/time-format'

interface ChannelInfo {
  account_name: string | null
  profile_picture_url?: string | null
  /** External page/app of the channel (business page, bot, embedded site), or null. */
  channel_url?: string | null
}

interface MessageThreadProps {
  conversation: ConversationDetail | null
  messages: ConversationMessage[]
  isLoading: boolean
  hasMore: boolean
  replyingAs?: ChannelInfo | null
  onLoadOlder: () => void
  onRefresh: () => Promise<void>
  onSendMessage: (text: string) => Promise<unknown>
  onToggleAiPaused: (paused: boolean) => Promise<void>
  /** Per-conversation opt-out from the agent's «pause AI on human intervention» setting. */
  onToggleAutoPauseExempt: (exempt: boolean) => Promise<void>
  onNewSession?: () => Promise<void>
  /** Whether the contact is on the agent's AI exclusion list. Not the same question as
   *  «is this conversation paused» — an operator can lift that pause and the entry stands. */
  contactExcluded?: boolean
  /** Inbox exclusion action — adds the contact to the agent's AI exclusion list and pauses. */
  onExcludeContact?: () => Promise<void>
  /** Inverse of the above, offered when the contact is already on the list. */
  onIncludeContact?: () => Promise<void>
  timezone: string
  /** Below `split` the thread replaced the list; this is the way back. */
  onBack?: () => void
}

interface OptionalHandlers {
  onNewSession?: () => Promise<void>
  onExcludeContact?: () => Promise<void>
  onIncludeContact?: () => Promise<void>
}

/**
 * The three actions this surface renders only when its caller wired them. The
 * rest need no callback, so they are absent here rather than mapped to `true`.
 */
const REQUIRED_HANDLER: Partial<Record<ThreadActionId, keyof OptionalHandlers>> = {
  exclude_contact: "onExcludeContact",
  include_contact: "onIncludeContact",
  new_session: "onNewSession",
}

function isWired(action: ThreadAction, handlers: OptionalHandlers): boolean {
  const required = REQUIRED_HANDLER[action.id]
  return !required || !!handlers[required]
}

export function MessageThread({
  conversation,
  messages,
  isLoading,
  hasMore,
  replyingAs,
  onLoadOlder,
  onRefresh,
  onSendMessage,
  onToggleAiPaused,
  onToggleAutoPauseExempt,
  onNewSession,
  contactExcluded,
  onExcludeContact,
  onIncludeContact,
  timezone,
  onBack,
}: MessageThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = useRef(0)
  const prevFirstIdRef = useRef<string | null>(null)
  const prevScrollHeightRef = useRef(0)
  const scrolledConversationRef = useRef<string | null>(null)
  const loadMoreRef = useRef<HTMLButtonElement>(null)
  const isInitialLoadRef = useRef(true)
  const wasAtBottomRef = useRef(true)

  const [pendingActionId, setPendingActionId] = useState<ThreadActionId | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshSuccess, setRefreshSuccess] = useState(false)
  const { can } = usePermissions()
  const canViewAiLogs = can(AI_PERMISSIONS.VIEW_LOGS)
  const canManageAgents = can(MESSAGING_AGENTS_PERMISSIONS.MANAGE)
  const [showNewSessionConfirm, setShowNewSessionConfirm] = useState(false)
  const [showExcludeConfirm, setShowExcludeConfirm] = useState(false)
  const [showIncludeConfirm, setShowIncludeConfirm] = useState(false)
  const [correctingOutcome, setCorrectingOutcome] = useState(false)

  // Track whether user is scrolled near the bottom
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    const handleScroll = () => {
      const threshold = 100
      wasAtBottomRef.current =
        container.scrollTop + container.clientHeight >= container.scrollHeight - threshold
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [conversation?.id])

  // Auto-scroll to bottom on initial load, conversation switch, and polled messages
  useLayoutEffect(() => {
    const container = scrollRef.current
    if (!container) return

    // Reset here, not in a separate effect: that one runs AFTER this one, and on the first
    // load it put `isInitialLoad` back to true — so the next prepend was read as a first load
    // and jumped to the newest message.
    const conversationId = conversation?.id ?? null
    if (conversationId !== scrolledConversationRef.current) {
      scrolledConversationRef.current = conversationId
      isInitialLoadRef.current = true
      prevMessagesLengthRef.current = 0
      prevFirstIdRef.current = null
      prevScrollHeightRef.current = 0
      wasAtBottomRef.current = true
    }

    const firstId = messages[0]?.id ?? null
    const action = resolveThreadScrollAction({
      messageCount: messages.length,
      previousCount: prevMessagesLengthRef.current,
      firstIdChanged: firstId !== prevFirstIdRef.current,
      isInitialLoad: isInitialLoadRef.current,
      wasAtBottom: wasAtBottomRef.current,
      hasMore,
    })

    if (action === 'bottom') {
      container.scrollTop = container.scrollHeight
      isInitialLoadRef.current = false
    } else if (action === 'preserve') {
      // prevScrollHeightRef holds the height before this render, so the delta is exactly
      // what was prepended above the viewport.
      container.scrollTop += container.scrollHeight - prevScrollHeightRef.current
    } else if (action === 'load-more') {
      container.scrollTop += container.scrollHeight - prevScrollHeightRef.current
      loadMoreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    prevMessagesLengthRef.current = messages.length
    prevFirstIdRef.current = firstId
    prevScrollHeightRef.current = container.scrollHeight
  }, [messages, conversation?.id, hasMore])

  const handleRefresh = async () => {
    if (isRefreshing) return
    setIsRefreshing(true)
    setRefreshSuccess(false)
    try {
      await onRefresh()
      setRefreshSuccess(true)
      setTimeout(() => setRefreshSuccess(false), 1500)
    } catch {
      // Refresh errors handled silently
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleNewSessionConfirm = async () => {
    if (!onNewSession) return
    setShowNewSessionConfirm(false)
    await runAction('new_session', onNewSession)
  }

  /**
   * The header reports a press; what it means is decided here.
   *
   * The two exclusion actions open this surface's own dialog rather than the
   * descriptor's plain question — it names the handle or number the list will
   * actually match on, which a generic confirmation cannot.
   */
  const handleAction = (action: ThreadAction) => {
    if (!conversation) return
    switch (action.id) {
      case 'pause_ai':
      case 'resume_ai':
        void runAction(action.id, () => onToggleAiPaused(!conversation.ai_paused))
        return
      case 'exempt_auto_pause':
      case 'clear_auto_pause_exemption':
        void runAction(action.id, () => onToggleAutoPauseExempt(!conversation.auto_pause_exempt))
        return
      case 'exclude_contact':
        setShowExcludeConfirm(true)
        return
      case 'include_contact':
        setShowIncludeConfirm(true)
        return
      case 'new_session':
        setShowNewSessionConfirm(true)
        return
    }
  }

  const runAction = async (id: ThreadActionId, perform: () => Promise<void>) => {
    if (pendingActionId) return
    setPendingActionId(id)
    try {
      await perform()
    } catch {
      // The caller surfaces its own failure; the state simply did not change.
    } finally {
      setPendingActionId(null)
    }
  }

  // Loading state (first conversation click — no previous conversation loaded)
  if (!conversation && isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-background flex-shrink-0">
          {onBack && <InboxBackButton onBack={onBack} />}
          <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
            <div className="h-3 w-20 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-3 bg-background">
          <div className="max-w-2xl mx-auto px-4 space-y-3 py-4 animate-in fade-in duration-300">
            {[...Array(4)].map((_, i) => (
              <div key={i} className={cn('flex', i % 2 === 0 ? 'justify-start' : 'justify-end')}>
                <div className={cn(
                  'rounded-2xl h-10 animate-pulse',
                  i % 2 === 0
                    ? 'bg-gray-200 dark:bg-gray-700 w-48'
                    : 'bg-blue-200 dark:bg-blue-900 w-56',
                )} />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!conversation) {
    return (
      <ConversationEmptyState
        title="Pick a conversation"
        description="Choose one from the list to read it here."
        // Below `split` this is also where a link to a conversation that failed to
        // load lands — with the list hidden, the arrow is the only way out.
        footer={onBack && <InboxBackButton onBack={onBack} />}
      />
    )
  }

  const contactTitle = resolveContactTitle(conversation)
  const isExcluded = contactExcluded ?? false
  const conversationState: MessagingConversationState = {
    aiPaused: conversation.ai_paused,
    pausedBy: conversation.ai_pause_by,
    pauseReason: conversation.ai_pause_reason,
    autoPauseExempt: conversation.auto_pause_exempt,
    contactExcluded: isExcluded,
  }
  // Filtered by the permission every one of these routes enforces, and by whether
  // the surface wired the callback at all. The AI toggle used to skip the first
  // check and answer 403 for a view-only operator.
  const actions = messagingThreadActions(conversationState).filter(
    (action) => canManageAgents && isWired(action, { onNewSession, onExcludeContact, onIncludeContact }),
  )
  const exclusionKey = exclusionIdentity(conversation)
  const displayedKey =
    exclusionKey && exclusionKey.value.toLowerCase() !== contactTitle.title.toLowerCase()
      ? exclusionKey.kind === 'handle' ? `@${exclusionKey.value}` : exclusionKey.value
      : null

  return (
    <div key={conversation.id} className="flex flex-col h-full animate-in fade-in duration-200">
      <ConversationHeader
        onBack={onBack}
        avatarUrl={conversation.contact_avatar_url ?? null}
        title={contactTitle.title}
        channelKind={conversation.channel_type}
        state={[messagingStatusBadge(conversation.status)]}
        actions={actions}
        banners={messagingThreadBanners(conversationState)}
        facts={sessionSummaryFacts(conversation.quality)}
        quality={conversation.quality ?? undefined}
        onCorrectQuality={
          canManageAgents ? () => setCorrectingOutcome(true) : undefined
        }
        pendingActionId={pendingActionId}
        onAction={handleAction}
        identity={
          <>
            {(displayedKey || conversation.channel_account_name || conversation.shopify_demo_domain) && (
              <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                {displayedKey && (
                  <span className="font-mono" title="This is what the AI exclusion list matches on">
                    {displayedKey}
                  </span>
                )}
                {displayedKey && conversation.channel_account_name && <span aria-hidden>·</span>}
                {conversation.channel_account_name && (
                  <span>via {conversation.channel_account_name}</span>
                )}
                {conversation.shopify_demo_domain && (
                  <>
                    {(displayedKey || conversation.channel_account_name) && <span aria-hidden>·</span>}
                    <span className="inline-flex items-center gap-1" title="Visitor-selected Shopify storefront domain">
                      <span aria-hidden>🏪</span>
                      <code className="bg-muted px-1 rounded text-[11px] font-mono">
                        {conversation.shopify_demo_domain}
                      </code>
                    </span>
                  </>
                )}
              </p>
            )}
            {(conversation.customer_info?.email || contactTitle.phone || conversation.crm_opportunity_url) && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {conversation.customer_info?.email && (
                  <span className="flex items-center gap-1 truncate">
                    <Mail className="h-3 w-3 flex-shrink-0" />
                    {conversation.customer_info.email}
                  </span>
                )}
                {contactTitle.phone && (
                  <span className="flex items-center gap-1 truncate">
                    <Phone className="h-3 w-3 flex-shrink-0" />
                    {contactTitle.phone}
                  </span>
                )}
                {conversation.crm_opportunity_url && (
                  <a
                    href={conversation.crm_opportunity_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open current session lead in CRM"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    <span>CRM</span>
                  </a>
                )}
              </div>
            )}
            {/* Always shown for log viewers: an anonymous web chat has no email, phone
                or CRM link, and that is exactly the conversation worth investigating. */}
            {canViewAiLogs && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <a
                  href={`?page=ai-logs&conversation_id=${conversation.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Every AI call this conversation produced, including turns that sent nothing"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                >
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  <span>AI logs</span>
                </a>
              </div>
            )}
          </>
        }
      >
        <Button
          variant="ghost"
          size="icon"
          className={HEADER_CONTROL_STYLES.button}
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Refresh"
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : refreshSuccess ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className={HEADER_CONTROL_STYLES.label}>Refresh</span>
        </Button>
      </ConversationHeader>

      {!conversation.ai_paused && conversation.pending_follow_up_at && (
        <div className="px-4 py-2 bg-blue-50 dark:bg-blue-950/30 border-b text-xs text-blue-700 dark:text-blue-400 flex items-center gap-2 flex-shrink-0">
          <Clock className="h-3.5 w-3.5" />
          <span>Follow-up scheduled for</span> {formatFollowUpTime(conversation.pending_follow_up_at, timezone)}<span>. A reply
          from the customer cancels it.</span>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 bg-background">
        {isLoading && messages.length === 0 ? (
          <div className="max-w-2xl mx-auto px-4 space-y-3 py-4 animate-in fade-in duration-300">
            {/* Skeleton loader — mimics message bubbles */}
            {[...Array(4)].map((_, i) => (
              <div key={i} className={cn('flex', i % 2 === 0 ? 'justify-start' : 'justify-end')}>
                <div className={cn(
                  'rounded-2xl h-10 animate-pulse',
                  i % 2 === 0
                    ? 'bg-gray-200 dark:bg-gray-700 w-48'
                    : 'bg-blue-200 dark:bg-blue-900 w-56',
                )} />
              </div>
            ))}
          </div>
        ) : (
          <div key={conversation.id} className="max-w-2xl mx-auto px-4 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* space-y-6 against gap-0.5 inside a bubble: the trailer (delivery
                ticks, badges, log link) hangs under its own message, and anything
                tighter lets it read as a header for the NEXT one. */}
            {hasMore && (
              <div className="flex justify-center py-2">
                <Button
                  ref={loadMoreRef}
                  variant="ghost"
                  size="sm"
                  onClick={onLoadOlder}
                  disabled={isLoading}
                  className="text-xs"
                >
                  {isLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : null}
                  <span>Load older messages</span>
                </Button>
              </div>
            )}

            {groupByDayInZone(
              transcriptRows(messages),
              timezone,
              (row) => row.message.created_at,
            ).map((group) => (
              <section key={group.key} className="space-y-6">
                <ConversationDateSeparator date={group.items[0].message.created_at} timezone={timezone} />
                {group.items.map(({ message: msg, event }) => (
                  <div key={msg.id}>
                    {event ? (
                      <ConversationEventLine
                        event={event}
                        crmUrl={(msg as unknown as Record<string, unknown>).crm_url as string | undefined}
                      />
                    ) : (
                      <MessageBubble
                        message={msg}
                        channelType={conversation.channel_type}
                        timezone={timezone}
                        contactLabel={contactTitle.title}
                      />
                    )}
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Gated on the permission the send route enforces: a composer an operator
          cannot send from is a message they write and cannot deliver. */}
      {canManageAgents && (
      <ConversationComposer
        // Remounted per conversation: the draft and the failure it may carry
        // belong to the conversation they were written for.
        key={conversation.id}
        channelKind={conversation.channel_type}
        identity={
          replyingAs?.account_name
            ? {
                accountName: replyingAs.account_name,
                avatarUrl: replyingAs.profile_picture_url,
                url: replyingAs.channel_url,
              }
            : null
        }
        onSend={onSendMessage}
      />
      )}

      {onExcludeContact && (
        <ExcludeContactDialog
          open={showExcludeConfirm}
          onOpenChange={setShowExcludeConfirm}
          contactName={contactTitle.title}
          onConfirm={onExcludeContact}
        />
      )}

      {onIncludeContact && (
        <ExcludeContactDialog
          mode="include"
          open={showIncludeConfirm}
          onOpenChange={setShowIncludeConfirm}
          contactName={contactTitle.title}
          onConfirm={onIncludeContact}
        />
      )}

      {conversation.quality && (
        // This inbox polls its own data rather than reading React Query, so the refresh the
        // header already owns is what brings the corrected verdict back.
        <OutcomeCorrectionDialog
          quality={conversation.quality}
          open={correctingOutcome}
          onOpenChange={setCorrectingOutcome}
          onCorrected={onRefresh}
        />
      )}

      <AlertDialog open={showNewSessionConfirm} onOpenChange={setShowNewSessionConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start New Session</AlertDialogTitle>
            <AlertDialogDescription>
              This will start a new session for this conversation. AI auto-reply will be re-enabled and
              a session separator will appear in the chat. The previous session&apos;s CRM lead will be kept as-is.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleNewSessionConfirm}>
              Start New Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** Same-day follow-ups show a time, later ones a short date. */
function formatFollowUpTime(iso: string, timezone: string): string {
  return isTodayInZone(iso, timezone)
    ? formatClockTime(iso, timezone)
    : new Date(iso).toLocaleString([], {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: timezone,
      })
}

interface TranscriptRow {
  message: ConversationMessage
  event: ConversationEvent | null
}

/** Each row with its event narrowed once, decisions drawn under the message they answered. */
function transcriptRows(messages: ConversationMessage[]): TranscriptRow[] {
  const rows = messages.filter(isRenderableMessage).map((message) => ({
    message,
    event: toConversationEvent({
      messageType: message.message_type,
      contentText: message.content_text,
      contentPayload: message.content_payload,
    }),
  }))
  return placeAfterAnsweredMessages(rows, (row) => row.message.id, (row) => row.event?.answeredMessageId ?? null)
}
